import { useState, useCallback, useRef } from 'react'
import { cn } from '@/true-fsd/shared/utils/css'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import type { FontFamily } from '../types'
import { FontSelectorPopup } from './FontSelectorPopup'
import { FontIcon } from './icons/FontIcon'
import { isWebKit } from '../utils/browserDetection'
import classes from './FontSelectorButton.module.scss'

/**
 * Пропсы для компонента FontSelectorButton
 */
export interface FontSelectorButtonProps {
    /**
     * Текущий выбранный шрифт
     */
    value: FontFamily
    /**
     * Обработчик изменения шрифта
     */
    onChange?: (font: FontFamily) => void
    /**
     * Дополнительные CSS классы
     */
    className?: string
    /**
     * Отключена ли кнопка
     */
    disabled?: boolean
}

/**
 * Компонент кнопки выбора шрифта
 * При наведении отображает тултип "Шрифт"
 * При клике открывает попап с выбором шрифта
 */
export function FontSelectorButton({
    value,
    onChange,
    className,
    disabled = false,
}: FontSelectorButtonProps) {
    const [open, setOpen] = useState(false)

    // Счётчик для пересоздания Tooltip после закрытия попапа
    // Это сбрасывает внутреннее состояние Radix Tooltip
    const tooltipKeyRef = useRef(0)

    // Обработчик изменения состояния попапа
    const handleOpenChange = useCallback((newOpen: boolean) => {
        if (!newOpen && open) {
            // Попап закрывается - увеличиваем ключ для пересоздания Tooltip
            tooltipKeyRef.current += 1
        }
        setOpen(newOpen)
    }, [open])

    /**
     * Обработчик изменения шрифта
     */
    const handleFontChange = useCallback(
        (font: FontFamily) => {
            if (disabled || !onChange) {
                return
            }
            onChange(font)
        },
        [disabled, onChange]
    )

    // Для WebKit передаем явный цвет иконки
    const useWebKit = isWebKit()
    // Определяем цвет иконки: активное состояние или базовый цвет
    const iconColor = useWebKit 
        ? (open ? '#056fff' : '#94a6af')
        : undefined

    const button = (
        <button
            type="button"
            className={cn(classes.button, className, {
                [classes.buttonDisabled]: disabled,
                [classes.buttonActive]: open,
            })}
            disabled={disabled}
            aria-label="Выбрать шрифт"
            aria-expanded={open}
        >
            <FontIcon className={classes.icon} color={iconColor} />
        </button>
    )

    // Если есть onChange, оборачиваем в Popover и Tooltip
    if (onChange) {
        const popover = (
            <FontSelectorPopup
                value={value}
                onChange={handleFontChange}
                open={open}
                onOpenChange={handleOpenChange}
            >
                {button}
            </FontSelectorPopup>
        )

        return (
            <Tooltip 
                key={tooltipKeyRef.current} 
                delayDuration={300} 
                open={open ? false : undefined}
            >
                <TooltipTrigger asChild>
                    <div className={classes.wrapper}>
                        {popover}
                    </div>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={4} className="z-[60]">
                    Шрифт
                </TooltipContent>
            </Tooltip>
        )
    }

    // Если нет onChange, просто кнопка с тултипом
    return (
        <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="top" sideOffset={4} className="z-[60]">
                Шрифт
            </TooltipContent>
        </Tooltip>
    )
}
